import { Fragment, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowRight, Check, FileText, Mail, X } from 'lucide-react'
import { BackButton } from '@/components/back-button'
import { EvidenceDocuments, type EvidenceDocument } from '@/components/evidence-documents'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { ActionPopover } from '@/components/action-popover'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useActorName } from '@/hooks/use-actor-name'
import { formatApiErrorMessage } from '@/lib/api-error'
import { cn, formatDateOnly, formatMoney, parseUTCDate } from '@/lib/utils'
import { SenderAvatar } from '@/features/communications/message-row'
import { useMessageAttachments } from '@/features/communications/hooks'
import { formatMessageTime } from '@/features/communications/message-format'
import { highlight } from '@/features/communications/thread-display'
import { stripQuotedHtml, stripQuotedText } from '@/lib/email-body'
import {
  type OrderFinding,
  type OrderFindingEvidence,
  type OrderFindingField,
  useDecideOrderFinding,
  useOrderFinding,
} from '@/features/order-review/hooks'

function day(value: string | null | undefined) {
  if (!value) return null
  return parseUTCDate(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** The email's own words with its line breaks, without the quoted history below them. */
function emailText(message: NonNullable<OrderFindingEvidence['message']>) {
  const raw = message.body_text
    ? stripQuotedText(message.body_text)
    : message.body_html
      ? htmlText(stripQuotedHtml(message.body_html))
      : ''
  return raw.replace(/\r/g, '').replace(/\n{3,}/g, '\n\n').trim()
}

function htmlText(html: string) {
  const doc = new DOMParser().parseFromString(html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n\n'), 'text/html')
  doc.querySelectorAll('style, script, head').forEach((node) => node.remove())
  return doc.body.textContent ?? ''
}

/** The manufacturer's email, as it reads in the PO thread, with the words the change rests on marked. */
function SourceEmail({ entry }: { entry: OrderFindingEvidence }) {
  const message = entry.message
  const [open, setOpen] = useState(false)
  if (!message) {
    return entry.quote ? (
      <div className="flex gap-3">
        <SenderAvatar tone="counterpart" icon={Mail} />
        <blockquote className="min-w-0 break-words pt-1.5 text-sm">“{entry.quote}”</blockquote>
      </div>
    ) : null
  }
  const text = emailText(message)
  const quote = (entry.quote ?? '').toLowerCase().trim()
  const time = message.received_at || message.sent_at || message.created_at
  return (
    <div className="flex gap-3">
      <SenderAvatar tone="counterpart" name={message.from_name || message.from_email || undefined} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-semibold">{message.from_name || message.from_email}</span>
          {message.from_name && message.from_email && (
            <span className="text-xs text-muted-foreground">{message.from_email}</span>
          )}
          {time && (
            <time dateTime={time} className="ml-auto text-xs text-muted-foreground tabular-nums">
              {formatMessageTime(time)}
            </time>
          )}
        </p>
        {message.subject && <p className="text-xs font-medium text-muted-foreground">{message.subject}</p>}
        <p className={cn('whitespace-pre-line break-words text-sm leading-relaxed', !open && 'line-clamp-5')}>
          {highlight(text, quote && text.toLowerCase().includes(quote) ? quote : '')}
        </p>
        <p className="flex gap-3 text-xs">
          {text.length > 280 && (
            <button
              type="button"
              onClick={() => setOpen((shown) => !shown)}
              className="cursor-pointer font-medium text-muted-foreground hover:text-foreground"
            >
              {open ? 'Show less' : 'Show the whole email'}
            </button>
          )}
          {entry.po_uuid && (
            <Link
              to="/purchase-orders/$poId"
              params={{ poId: entry.po_uuid }}
              search={{ message: String(message.id) }}
              className="font-medium text-muted-foreground hover:text-foreground"
            >
              Open in the PO thread
            </Link>
          )}
        </p>
      </div>
    </div>
  )
}

/** The supplier invoice: its header and lines, the line the change rests on marked. */
function SourceInvoice({ entry }: { entry: OrderFindingEvidence }) {
  const invoice = entry.invoice
  if (!invoice) {
    return entry.invoice_line ? (
      <div className="flex gap-3">
        <SenderAvatar tone="counterpart" icon={FileText} />
        <p className="min-w-0 pt-1.5 text-sm">
          <span className="font-medium">Invoice {entry.invoice_number}</span>
          <span className="ml-2 font-mono text-xs">{entry.invoice_line}</span>
        </p>
      </div>
    ) : null
  }
  const lines = invoice.lines ?? []
  return (
    <div className="flex gap-3">
      <SenderAvatar tone="counterpart" icon={FileText} />
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <Link
            to="/invoices/$invoiceId"
            params={{ invoiceId: invoice.uuid }}
            className="font-semibold underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
          >
            Invoice {invoice.invoice_number}
          </Link>
          {invoice.invoice_date && (
            <span className="text-xs text-muted-foreground">{formatDateOnly(invoice.invoice_date)}</span>
          )}
          <span className="ml-auto text-sm font-medium tabular-nums">{formatMoney(invoice.invoice_total)}</span>
        </p>
        {invoice.payment_terms_raw && (
          <p className="text-xs text-muted-foreground">Terms: {invoice.payment_terms_raw}</p>
        )}
        {entry.invoice_line && !lines.some((line) => line.cited) && (
          <p className="rounded-md bg-amber-100/70 px-2 py-1.5 font-mono text-xs dark:bg-amber-500/20">
            {entry.invoice_line}
          </p>
        )}
        {lines.length > 0 && (
          <ul className="divide-y text-sm">
            {lines.map((line) => (
              <li
                key={line.position}
                className={cn(
                  'grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 px-2 py-1.5 sm:grid-cols-[8rem_minmax(0,1fr)_auto]',
                  line.cited && 'rounded-md bg-amber-100/70 dark:bg-amber-500/20',
                )}
              >
                <span className="font-mono text-xs leading-5 max-sm:col-span-2">{line.model_number || '—'}</span>
                <span className="min-w-0 break-words text-xs leading-5 text-muted-foreground">
                  {line.description}
                  {line.quantity != null && line.unit_cost != null && (
                    <span className="tabular-nums">
                      {' · '}
                      {Number(line.quantity)} × {formatMoney(line.unit_cost)}
                    </span>
                  )}
                </span>
                <span className="text-right text-xs leading-5 tabular-nums">{formatMoney(line.line_total)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/** The files behind the evidence, opened in place: the PO and quote PDFs, the invoice, the email's attachments. */
function EvidenceFiles({ entry }: { entry: OrderFindingEvidence }) {
  const message = entry.message
  const attachments = useMessageAttachments(message?.uuid ?? undefined, !!message?.has_attachments)
  const documents: EvidenceDocument[] = [
    ...(entry.documents ?? []).map((document) => ({
      key: document.path,
      label: document.kind === 'invoice_file' ? `${document.label} · ${document.filename}` : document.label,
      filename: document.filename,
      contentType: document.content_type,
      path: document.path,
    })),
    ...(message?.uuid
      ? (attachments.data ?? [])
          // Embedded images only when they are pictures; signature logos and the like are noise.
          .filter((attachment) => !attachment.is_inline || attachment.content_type.startsWith('image/'))
          .map((attachment) => {
            const path = `/dashboard/communications/messages/${message.uuid}/attachments/${encodeURIComponent(attachment.id)}`
            return {
              key: path,
              label: attachment.name,
              filename: attachment.name,
              contentType: attachment.content_type,
              path,
              sheetPath: `${path}/sheet`,
            }
          })
      : []),
  ]
  return <EvidenceDocuments documents={documents} className="pl-11" />
}

/** One purchase order that showed the change: what happened, then its sources as they read. */
function EvidenceItem({ entry }: { entry: OrderFindingEvidence }) {
  return (
    <li className="space-y-3 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        {entry.po_uuid ? (
          <Link
            to="/purchase-orders/$poId"
            params={{ poId: entry.po_uuid }}
            className="font-mono text-sm font-medium underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
          >
            {entry.po_number}
          </Link>
        ) : (
          <span className="font-mono text-sm font-medium">{entry.po_number}</span>
        )}
        {entry.seen_on && (
          <span className="text-xs text-muted-foreground tabular-nums">Reviewed {formatDateOnly(entry.seen_on)}</span>
        )}
      </div>
      {entry.observed && <p className="text-sm leading-snug">{entry.observed}</p>}
      <SourceEmail entry={entry} />
      <SourceInvoice entry={entry} />
      <EvidenceFiles entry={entry} />
    </li>
  )
}

/** A field's value as staff read it: money as dollars, a percent with %, a choice by its name. */
function shownValue(field: OrderFindingField, value: string | null | undefined) {
  if (value == null || value === '') return null
  if (field.kind === 'select') {
    const labels = new Map((field.options ?? []).map(([optionValue, label]) => [optionValue, label]))
    return value
      .split(',')
      .map((part) => labels.get(part.trim()) ?? part.trim())
      .join(', ')
  }
  if (field.kind === 'money') return formatMoney(value)
  if (field.kind === 'percent') return `${value}%`
  return value
}

/** What is on file, crossed out, then an arrow: the "before" of a before → after row. */
function Before({ text }: { text: string | null }) {
  return (
    <>
      <s className="shrink-0 text-muted-foreground decoration-muted-foreground/70">{text ?? 'None'}</s>
      <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" aria-label="changes to" />
    </>
  )
}

/** One proposed value, edited in place like the PO page's fields. */
function FieldValue({
  field,
  value,
  onChange,
  disabled,
}: {
  field: OrderFindingField
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const options = (field.options ?? []).map(([optionValue, label]) => ({ value: optionValue, label }))
  const display = shownValue(field, value) ?? undefined
  return (
    <POInlineEditableField
      value={value}
      displayValue={display}
      placeholder={field.required ? 'Add' : 'None'}
      kind={field.kind === 'select' ? 'select' : field.kind === 'money' ? 'money' : field.kind === 'text' ? 'text' : 'number'}
      options={options}
      ariaLabel={field.label}
      title={`Edit ${field.label.toLowerCase()}`}
      invalid={field.required && !value.trim()}
      disabled={disabled}
      showEditIcon
      allowWrap
      buttonClassName="-ml-2 min-w-0"
      onConfirm={onChange}
    />
  )
}

/** Inputs the chosen option uses: "Net" asks for net days, "Prepaid" for nothing; a minimum's fee only for a fee. */
const SHOWN_WITH: Record<string, Record<string, readonly string[]>> = {
  payment_terms: {
    net: ['payment_terms_type', 'net_days', 'text'],
    discount_net: ['payment_terms_type', 'net_days', 'discount_percent', 'discount_days', 'text'],
    prepaid: ['payment_terms_type', 'deposit_percent', 'text'],
    due_on_receipt: ['payment_terms_type', 'text'],
    proximo: ['payment_terms_type', 'proximo_day', 'text'],
  },
  minimum_order: {
    fixed_fee: ['policy_type', 'threshold', 'fee'],
    top_up: ['policy_type', 'threshold'],
    hard_minimum: ['policy_type', 'threshold'],
  },
}

function shownFields(kind: string, fields: OrderFindingField[], values: Record<string, string>) {
  const byChoice = SHOWN_WITH[kind]
  if (!byChoice) return fields
  const choice = values[kind === 'payment_terms' ? 'payment_terms_type' : 'policy_type'] ?? ''
  const keys = byChoice[choice]
  return keys ? fields.filter((field) => keys.includes(field.key)) : fields
}

function Decided({ finding }: { finding: OrderFinding }) {
  const actorName = useActorName()
  const applied = finding.status === 'applied'
  const who = actorName(finding.decided_by) ?? 'Staff'
  return (
    <div className="mt-3 space-y-1 border-t pt-3 text-sm">
      <p>
        <span className="font-medium">{applied ? (finding.kind === 'other' ? 'Done' : 'Applied') : 'Dismissed'}</span>
        <span className="text-muted-foreground">
          {' '}
          by {who}
          {finding.decided_at ? ` · ${day(finding.decided_at)}` : ''}
        </span>
      </p>
      {finding.change?.status === 'reverted' && <p className="text-muted-foreground">Reverted since.</p>}
      {finding.decision_note && <p className="text-muted-foreground">“{finding.decision_note}”</p>}
    </div>
  )
}

/** The change: what we have, the values approving applies (click to edit), Approve or Dismiss, the decision. */
function ChangePanel({ finding }: { finding: OrderFinding }) {
  const decide = useDecideOrderFinding(finding.uuid)
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((finding.fields ?? []).map((field) => [field.key, field.value ?? ''])),
  )
  const open = finding.status === 'open'
  const isOther = finding.kind === 'other'
  const fields = shownFields(finding.kind, finding.fields ?? [], values)
  const missing = fields.some((field) => field.required && !values[field.key]?.trim())
  const failed = (title: string) => (error: unknown) => toast.error(formatApiErrorMessage(title, error))
  const editing = open && fields.length > 0

  const approve = () =>
    decide.mutate(
      {
        kind: 'approve',
        values: isOther
          ? undefined
          : Object.fromEntries(fields.map((field) => [field.key, values[field.key]?.trim() || null])),
      },
      {
        onSuccess: () => toast.success(isOther ? 'Marked done' : 'Change applied'),
        onError: failed(isOther ? 'Not saved' : 'Not applied'),
      },
    )
  const dismiss = (note: string, close: () => void) => {
    close()
    decide.mutate(
      { kind: 'dismiss', notes: note },
      { onSuccess: () => toast.success('Dismissed'), onError: failed('Not dismissed') },
    )
  }

  return (
    <section aria-labelledby="finding-change" className="rounded-lg border bg-card p-4 lg:sticky lg:top-4">
      <h2 id="finding-change" className="sr-only">
        Change
      </h2>
      <dl className="grid grid-cols-[8rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 text-sm">
        {editing ? (
          fields.map((field) => {
            const before = shownValue(field, field.before)
            const unchanged = (field.before ?? '') === (values[field.key] ?? '')
            return (
              <Fragment key={field.key}>
                <dt className="text-muted-foreground">{field.label}</dt>
                <dd className="flex min-w-0 items-center gap-x-2">
                  {!unchanged && <Before text={before} />}
                  <span className="min-w-0 flex-1">
                    <FieldValue
                      field={field}
                      value={values[field.key] ?? ''}
                      disabled={decide.isPending}
                      onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))}
                    />
                  </span>
                </dd>
              </Fragment>
            )
          })
        ) : isOther ? (
          <>
            <dt className="text-muted-foreground">To update</dt>
            <dd className="min-h-8 break-words py-1">{finding.proposed_text}</dd>
          </>
        ) : (
          <>
            <dt className="text-muted-foreground">{finding.kind_label}</dt>
            <dd className="flex min-h-8 min-w-0 flex-wrap items-center gap-x-2 py-1">
              <Before text={finding.on_file_text} />
              <span className={cn('font-medium', finding.status === 'dismissed' && 'text-muted-foreground')}>
                {finding.proposed_text}
              </span>
            </dd>
          </>
        )}
      </dl>

      {open && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
          <LoadingButton
            size="sm"
            className="bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-600/40 dark:bg-emerald-600 dark:hover:bg-emerald-500"
            loading={decide.isPending && decide.variables?.kind === 'approve'}
            loadingText={isOther ? 'Saving...' : 'Applying...'}
            disabled={missing || decide.isPending}
            onClick={approve}
          >
            <Check className="size-3.5" aria-hidden="true" /> {isOther ? 'Mark done' : 'Approve'}
          </LoadingButton>
          <ActionPopover
            trigger={
              <LoadingButton
                size="sm"
                variant="outline"
                loading={decide.isPending && decide.variables?.kind === 'dismiss'}
                loadingText="Dismissing..."
                disabled={decide.isPending}
              >
                <X className="size-3.5" aria-hidden="true" /> Dismiss
              </LoadingButton>
            }
            note={{ label: 'Dismissal note', placeholder: 'Why dismiss? (optional)', maxLength: 1000 }}
            confirmLabel="Dismiss"
            onConfirm={dismiss}
          />
        </div>
      )}

      {!open && <Decided finding={finding} />}
    </section>
  )
}

function FindingSkeleton() {
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-8 w-96 max-w-full" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Skeleton className="h-64" />
        <Skeleton className="h-48" />
      </div>
    </div>
  )
}

/** A Data item's order review finding (USA-1513): the change the orders showed, and Approve or Dismiss. */
export function OrderReviewFindingPage({ findingId }: { findingId: string }) {
  const { data: finding, isLoading, error } = useOrderFinding(findingId)
  if (isLoading) return <FindingSkeleton />
  if (error || !finding) {
    return <p className="text-sm text-muted-foreground">This finding could not be loaded. Try again shortly.</p>
  }
  const evidence = finding.evidence ?? []
  return (
    <div className="mx-auto min-w-0 max-w-6xl">
      <nav className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        <BackButton iconOnly fallback={{ to: '/order-review' }} fallbackLabel="Back to Order review" />
        {finding.manufacturer && (
          <Link
            to="/manufacturers/$manuId"
            params={{ manuId: finding.manufacturer.uuid }}
            search={{ tab: 'emails' }}
            className="min-w-0 truncate hover:text-foreground hover:underline"
          >
            {finding.manufacturer.name}
          </Link>
        )}
      </nav>
      <div className="mb-5 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <h1 className="break-words text-lg font-bold tracking-tight sm:text-xl md:text-2xl">{finding.title}</h1>
        <Badge variant="outline">{finding.kind_label}</Badge>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 lg:order-2">
          <ChangePanel key={`${finding.uuid}-${finding.status}`} finding={finding} />
        </div>
        <section aria-labelledby="finding-orders" className="min-w-0 space-y-2 lg:order-1">
          <h2 id="finding-orders" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            What the orders showed
          </h2>
          <ul className="divide-y border-y">
            {evidence.map((entry, index) => (
              <EvidenceItem key={`${entry.purchase_order_id ?? index}-${index}`} entry={entry} />
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
